import React from 'react'
import _ from 'lodash'
import { useDispatch } from 'react-redux'
import { Tabs, Tab, Box, Card, Paper } from '@mui/material'
import { UploadImageV2 } from './UploadImageV2'
import { TempAutoSelectInput } from '../Input/TempAutoSelectInput'
import { TempTextInput } from '../Input/TempTextInput'
import { TempBtn } from '../Input/TempBtn'
import { getString } from '../../helper/util'

import DeleteIcon from '@mui/icons-material/Delete'
import PhotoCamera from '@mui/icons-material/PhotoCamera'
import SwapVertIcon from '@mui/icons-material/SwapVert'
import PropTypes from 'prop-types'

import { setBrandValue } from '../../actions/brandAction'
import { setProductCategory, setProductValue } from '../../actions/productCreateAction'
import { setMerchantShopValue } from '../../actions/merchantShopCreateAction'
import { constants as cons, IMAGE_TYPE } from '../../Constants/constants'
import { setLoading } from '../../actions/systemAction'
import { UploadImage } from './UploadImage'

import I18nImageTabs from '../ProductCreate/Product/cashCouponImageList'


// Map product image type constants (from caller) to imageMedia keys
const defaultMapTypeToKey = (type) => {
  switch (type) {
    case 'ITEM_ICON':
      return 'iconImage'
    case 'ITEM_DETAIL':
      return 'detailImageList'
    case 'CASH_COUPON_DETAIL':
      return 'cashCouponDetailImage'
    case 'CASH_COUPON_PURCHASED_DETAIL':
      return 'cashCouponDetailPurchasedImage'
    case 'CASH_COUPON_LEFT_IMAGE':
      return 'cashCouponLeftImage'
    default:
      return null
  }
}

const LANGUAGE_KEY_PATTERN = /^[a-z]{2}(?:[-_][a-z]{2})?$/i
const DEFAULT_I18N_LANGUAGES = ['en', 'zh-HK', 'zh-TW', 'zh-CN']

const isLanguageKey = (key) => LANGUAGE_KEY_PATTERN.test(String(key || ''))

const normalizeImageLanguageKey = (languageKey) => {
  switch (languageKey) {
    case 'EN':
    case 'en':
      return 'en'
    case 'ZH_HK':
    case 'zh_HK':
    case 'zh-HK':
      return 'zh-HK'
    case 'ZH_TW':
    case 'zh_TW':
    case 'zh-TW':
      return 'zh-TW'
    case 'ZH_CN':
    case 'zh_CN':
    case 'zh-CN':
      return 'zh-CN'
    default:
      return languageKey
  }
}

const preserveImageIdentityFields = (existingValue, nextValue) => {
  if (!nextValue) {
    return nextValue
  }

  const preservedValue = {
    ...existingValue,
    ...nextValue,
  }

  if (existingValue?.id) {
    preservedValue.id = existingValue.id
  }

  if (existingValue?.batchId) {
    preservedValue.batchId = existingValue.batchId
  }

  return preservedValue
}

const buildReplicatedImageMap = (currentMap = {}, nextValue) => {
  return DEFAULT_I18N_LANGUAGES.reduce((accumulator, languageKey) => {
    accumulator[languageKey] = preserveImageIdentityFields(currentMap?.[languageKey], _.cloneDeep(nextValue))
    return accumulator
  }, {})
}

const createEmptyI18nImageMap = () => {
  return DEFAULT_I18N_LANGUAGES.reduce((accumulator, languageKey) => {
    accumulator[languageKey] = {
      cosFileKey: null,
      cosFileUrl: null,
      type: 'ITEM_DETAIL',
      status: 'A',
      displaySequence: 10,
    }
    return accumulator
  }, {})
}

// Normalize a tab's value from imageMedia into an array for rendering
const getListForTab = (imageMedia = {}, tabKey) => {
  if (!tabKey) return []
  const value = imageMedia[tabKey]

  if (!value) return []

  // If value is an object: could be language-keyed map or single object
  if (value && !Array.isArray(value) && typeof value === 'object') {
    const keys = Object.keys(value)
    const looksLikeLangMap = keys.length > 0 && keys.every((key) => isLanguageKey(key))
    if (looksLikeLangMap) {
      return keys.map((lang) => ({ lang, data: value[lang] }))
    }
    // Single-image object (e.g., leftImage) -> return single entry with no lang
    return [{ lang: null, data: value }]
  }

  if (Array.isArray(value)) {
    // Legacy arrays - map to { lang: null, data }
    return value.map((d) => ({ lang: d.language || null, data: d }))
  }

  return []
}

export function ImageListCashCoupon(props) {
  const dispatch = useDispatch()
  // Using MUI v5: prefer `sx` on components instead of makeStyles
  const {
    type,
    typeList = [],
    savedData = {},
    onUploadImageList = () => {},
    uploadPath,
    disableInput,
    resource,
    mediaType,
    hiddenInput,
    validation,
    validationConstants,
    helperTextStylesClasses,
    hideTitle,
    // pass-through props for UploadImageV2
    ...rest
  } = props

  // Support both `imageMedia` and legacy `cashCouponMedia` on the product
  const product = (((savedData || {}).draft || {}).product) || {}
  const imageMedia = product.imageMedia || product.cashCouponMedia || {}
  // active tab index for UI
  const [tabIndex, setTabIndex] = React.useState(0)

  const handleChange = (e, newIndex) => setTabIndex(newIndex)

  const updateImageMedia = (tabKey, lang, updater) => {
    const nextImageMedia = _.cloneDeep(imageMedia || {})
    const currentValue = nextImageMedia[tabKey]
    const normalizedLang = lang ? normalizeImageLanguageKey(lang) : null

    const currentValueAsLanguageMap = () => {
      if (currentValue && !Array.isArray(currentValue) && typeof currentValue === 'object' && !(currentValue.cosFileUrl || currentValue.cosFileKey)) {
        return _.cloneDeep(currentValue)
      }

      if (currentValue && !Array.isArray(currentValue) && typeof currentValue === 'object') {
        return buildReplicatedImageMap({}, currentValue)
      }

      return {}
    }

    if (normalizedLang) {
      const currentMap = currentValue && !Array.isArray(currentValue) && typeof currentValue === 'object' && !(currentValue.cosFileUrl || currentValue.cosFileKey)
        ? _.cloneDeep(currentValue)
        : {}

      const nextValue = updater(currentMap[normalizedLang], currentMap)

      if (tabKey === 'iconImage' || tabKey === 'cashCouponLeftImage') {
        if (nextValue) {
          nextImageMedia[tabKey] = buildReplicatedImageMap(currentMap, nextValue)
        } else {
          delete nextImageMedia[tabKey]
        }
      } else {
        if (nextValue) {
          currentMap[normalizedLang] = nextValue
        } else {
          delete currentMap[normalizedLang]
        }

        nextImageMedia[tabKey] = currentMap
      }
    } else {
      const nextValue = updater(currentValue, currentValue)
      if (nextValue) {
        if (tabKey === 'iconImage' || tabKey === 'cashCouponLeftImage') {
          const currentMap = currentValueAsLanguageMap()

          nextImageMedia[tabKey] = buildReplicatedImageMap(currentMap, nextValue)
        } else {
          nextImageMedia[tabKey] = nextValue
        }
      } else {
        delete nextImageMedia[tabKey]
      }
    }

    dispatch(setProductValue(cons.ACTION_KEY.PRODUCT.PRODUCT, savedData, 'imageMedia', nextImageMedia))
  }

  const updateDetailImageList = (itemIndex, lang, updater) => {
    const normalizedLang = lang ? normalizeImageLanguageKey(lang) : 'en'
    const nextImageMedia = _.cloneDeep(imageMedia || {})
    const currentDetailList = Array.isArray(nextImageMedia.detailImageList)
      ? _.cloneDeep(nextImageMedia.detailImageList)
      : []

    while (currentDetailList.length <= itemIndex) {
      currentDetailList.push(createEmptyI18nImageMap())
    }

    const currentItem = currentDetailList[itemIndex] || createEmptyI18nImageMap()
    const currentLangValue = currentItem?.[normalizedLang]
    const nextLangValue = updater(currentLangValue, currentItem)

    if (nextLangValue) {
      const replicatedItem = DEFAULT_I18N_LANGUAGES.reduce((accumulator, languageKey) => {
        accumulator[languageKey] = preserveImageIdentityFields(currentItem?.[languageKey], _.cloneDeep(nextLangValue))
        return accumulator
      }, {})
      currentDetailList[itemIndex] = replicatedItem
    } else {
      if (currentDetailList.length > 1) {
        currentDetailList.splice(itemIndex, 1)
      } else {
        // currentDetailList[itemIndex] = createEmptyI18nImageMap()
        currentDetailList[itemIndex] = null
      }
    }

    const currentItemAfterUpdate = currentDetailList[itemIndex]
    const hasAnyImage = Object.values(currentItemAfterUpdate || {}).some((value) => value && (value.cosFileKey || value.cosFileUrl || value.id))
    if (!hasAnyImage && currentDetailList.length > 1) {
      currentDetailList.splice(itemIndex, 1)
    }
    nextImageMedia.detailImageList = (itemIndex == 0 && !currentDetailList[itemIndex]) ? null : currentDetailList
    dispatch(setProductValue(cons.ACTION_KEY.PRODUCT.PRODUCT, savedData, 'imageMedia', nextImageMedia))
  }

  function getUploadedImagePayload(nameOrPayload, uploadedImage) {
    if (uploadedImage && typeof uploadedImage === 'object' && !Array.isArray(uploadedImage)) {
      return uploadedImage
    }

    if (Array.isArray(nameOrPayload)) {
      return nameOrPayload[0] || null
    }

    if (nameOrPayload && typeof nameOrPayload === 'object') {
      if (nameOrPayload.picture && typeof nameOrPayload.picture === 'object') {
        return nameOrPayload.picture
      }

      if (nameOrPayload.cosFileKey || nameOrPayload.cosFileUrl) {
        return nameOrPayload
      }
    }

    return null
  }

  function onSaveImage(tabKey, lang, nameOrPayload, uploadedImage, itemIndex = 0) {
    const uploadedPayload = getUploadedImagePayload(nameOrPayload, uploadedImage)

    if (!uploadedPayload) {
      return
    }

    if (tabKey === 'detailImageList') {
      updateDetailImageList(itemIndex, lang, (currentValue) => ({
        ...(currentValue || {}),
        ...uploadedPayload,
        type: uploadedPayload?.type || currentValue?.type || dataTypeFromTabKey(tabKey) || tabKey,
      }))
      return
    }

    updateImageMedia(tabKey, lang, () => ({
      ...imageMedia?.[tabKey]?.[lang],
      ...uploadedPayload,
      type: uploadedPayload?.type || imageMedia?.[tabKey]?.[lang]?.type || dataTypeFromTabKey(tabKey) || tabKey,
      ...(tabKey === 'cashCouponLeftImage' ? { __updated: true } : {}),
    }))
  }

  function onChangeImageType(tabKey, lang, imageType, itemIndex = 0) {
    if (tabKey === 'detailImageList') {
      updateDetailImageList(itemIndex, lang, (currentValue) => currentValue ? { ...currentValue, type: imageType } : currentValue)
      return
    }

    updateImageMedia(tabKey, lang, (currentValue) => currentValue ? { ...currentValue, type: imageType } : currentValue)
  }

  function handleImageStatus(tabKey, lang, status, itemIndex = 0) {
    if (tabKey === 'detailImageList') {
      updateDetailImageList(itemIndex, lang, (currentValue) => currentValue ? { ...currentValue, status: status || '' } : currentValue)
      return
    }

    updateImageMedia(tabKey, lang, (currentValue) => currentValue ? { ...currentValue, status: status || '' } : currentValue)
  }

  function onDeleteImage(tabKey, lang, itemIndex = 0) {
    if (tabKey === 'detailImageList') {
      updateDetailImageList(itemIndex, lang, () => null)
      return
    }

    updateImageMedia(tabKey, lang, () => null)
  }

  function dataTypeFromTabKey(tabKey) {
    switch (tabKey) {
      case 'iconImage':
        return 'ITEM_ICON'
      case 'detailImage':
      case 'detailImageList':
        return 'ITEM_DETAIL'
      case 'cashCouponDetailImage':
        return 'CASH_COUPON_DETAIL'
      case 'cashCouponDetailPurchasedImage':
        return 'CASH_COUPON_PURCHASED_DETAIL'
      case 'leftImage':
      case 'cashCouponLeftImage':
        return 'CASH_COUPON_LEFT_IMAGE'
      default:
        return null
    }
  }

  // Render content for a tab (product-only behavior)
  const renderProductTab = (tabItem) => {
    const raw = typeof tabItem === 'string' ? tabItem : (tabItem && (tabItem.value || tabItem.key || tabItem))
    const tabKey = defaultMapTypeToKey(raw)
    if (!tabKey) return null

    const list = imageMedia[tabKey]

    if (tabKey === 'detailImageList') {
      // const detailList = Array.isArray(list) && list.length ? list : [createEmptyI18nImageMap()]
      const detailList = Array.isArray(list) && list.length ? list : null
      return (
        <div key={tabKey}>
          {
            detailList ? (
              <>
                {detailList.map((detailItem, detailIndex) => (
                  <Box key={`${tabKey}-${detailIndex}`} sx={{ mb: 2 }}>
                    <I18nImageTabs
                      templateData={detailItem}
                      tabKey={tabKey}
                      itemIndex={detailIndex}
                      onSaveImage={onSaveImage}
                      onDeleteImage={onDeleteImage}
                      onChangeImageType={onChangeImageType}
                      handleImageStatus={handleImageStatus}
                      {...props}
                    />
                  </Box>
                ))}

                {!disableInput && (
                  <Box sx={{ mt: 1 }}>
                    <TempBtn
                      btnSetting={{
                        className: 'edit-create-btn',
                        variant: cons.STYLE.VARIANT.CONTAINED,
                        color: cons.STYLE.PRIMARY,
                        label: { resource: 'productCreateProductList', key: 'add' },
                        onClick: () => {
                          const nextImageMedia = _.cloneDeep(imageMedia || {})
                          const currentDetailList = Array.isArray(nextImageMedia.detailImageList) ? nextImageMedia.detailImageList : []
                          currentDetailList.push(createEmptyI18nImageMap())
                          nextImageMedia.detailImageList = currentDetailList
                          dispatch(setProductValue(cons.ACTION_KEY.PRODUCT.PRODUCT, savedData, 'imageMedia', nextImageMedia))
                        },
                        disabled: disableInput,
                      }}
                    />
                  </Box>
                )}
              </>
            ) : (
              <Box>
                <UploadImageV2
                  {...rest}
                  picture={null}
                  type={type}
                  title={{ resource: 'UploadImage', key: 'image' }}
                  name="image"
                  uploadImagePath={uploadPath}
                  saveImage={(name, image) => onSaveImage(tabKey, null, name, image)}
                  disableInput={disableInput}
                />
              </Box>
            )
          }
        </div>
      )
    }

    return (
      <div 
      // className={classes.tabPanel} 
      key={tabKey}>
        {
          list ? (
            <I18nImageTabs
              templateData={list}
              onSaveImage={onSaveImage}
              onDeleteImage={onDeleteImage}
              onChangeImageType={onChangeImageType}
              handleImageStatus={handleImageStatus}
              tabKey={tabKey}
              {...props}
            />
          ) : (
            <Box>
              <UploadImageV2
                {...rest}
                picture={null}
                type={type}
                title={{ resource: 'UploadImage', key: 'image' }}
                name="image"
                uploadImagePath={uploadPath}
                saveImage={(name, image) => onSaveImage(tabKey, null, name, image)}
                disableInput={disableInput}
              />
            </Box>
          )
        }
        

        {/* {list.length ? (
          list.map((entry, index) => {
            const { lang, data } = entry
            const imageKey = data?.id || data?.cosFileKey || data?.cosFileUrl || 'new-image'
            const itemKey = `${tabKey}-${lang || 'default'}-${imageKey}-${index}`

            return (
              <Card key={itemKey} className="image-list image-card-background">
                  <div className="image-list-content">
                    {!showAll && <SwapVertIcon color={cons.STYLE.PRIMARY} fontSize={cons.SIZE.LARGE} />}
                    <div className="image-show">
                      <UploadImageV2
                        type={type}
                        picture={data}
                        title={{ resource: 'UploadImage', key: 'image' }}
                        name="image"
                        uploadImagePath={uploadPath}
                        saveImage={(name, image) => onSaveImage(tabKey, lang, name, image)}
                        index={index}
                        disableInput={disableInput}
                      />
                    </div>
                    {typeList && disableInput !== true ? (
                      <TempAutoSelectInput
                        inputSetting={{
                          className: 'edit-create-input',
                          size: cons.SIZE.SMALL,
                          variant: cons.STYLE.VARIANT.OUTLINED,
                          type: cons.TYPE.SELECT,
                          selectValue: typeList.filter((item) => item.value !== 'CATEGORY_ALL' && item.value !== 'ITEM_ALL'),
                          label: { resource: 'productCreateProductList', key: 'type' },
                          value: data.type,
                          disabled: disableInput,
                          disableClearable: true,
                          helperTextIcon: true,
                          helperText: validation
                            ?.getErrorMessages(
                              validationConstants.KEY_PRODUCT_IMAGE_TYPE,
                              savedData?.draft?.product?.productCode,
                              data.id ? data.id : data.cosFileKey
                            )
                            .map((error) => {
                              return error
                            }),
                          FormHelperTextProps: { className: helperTextStylesClasses.root },
                        }}
                        handleChange={(_name, imageType) => onChangeImageType(tabKey, lang, imageType)}
                        index={index}
                      />
                    ) : (
                      <TempTextInput
                        inputSetting={{
                          className: 'edit-create-input',
                          variant: cons.STYLE.VARIANT.STANDARD,
                          size: cons.SIZE.SMALL,
                          label: { resource: resource, key: 'type' },
                          value: data.type,
                          disabled: true,
                        }}
                        index={index}
                      />
                    )}
                    <TempTextInput
                      inputSetting={{
                        className: 'edit-create-input',
                        variant: cons.STYLE.VARIANT.STANDARD,
                        type: cons.TYPE.NUMBER,
                        min: -1,
                        size: cons.SIZE.SMALL,
                        label: { resource: resource, key: 'displaySequence' },
                        value: data.displaySequence,
                        disabled: true,
                      }}
                      index={index}
                    />
                    {!data.id ? (
                      !hiddenInput && (
                        <div className="edit-create-input">
                          <TempBtn
                            btnSetting={{
                              className: 'edit-create-btn',
                              variant: cons.STYLE.VARIANT.CONTAINED,
                              color: cons.STYLE.PRIMARY,
                              onClick: () => onDeleteImage(tabKey, lang),
                              label: { key: 'deleteBtn' },
                              icon: <DeleteIcon />,
                              disabled: disableInput,
                            }}
                          />
                        </div>
                      )
                    ) : !disableInput ? (
                      <TempAutoSelectInput
                        inputSetting={{
                          className: 'edit-create-input',
                          size: cons.SIZE.SMALL,
                          variant: cons.STYLE.VARIANT.OUTLINED,
                          type: cons.TYPE.SELECT,
                          selectValue: cons.CHOICES.PRODUCT_CREATE.CREATE_CATEGORY.STATUS,
                          label: { resource: resource, key: 'status' },
                          value: data.status ? data.status : '',
                          disabled: disableInput,
                        }}
                        handleChange={(_name, status) => handleImageStatus(tabKey, lang, status)}
                        index={index}
                      />
                    ) : (
                      <TempTextInput
                        inputSetting={{
                          className: 'edit-create-input',
                          variant: cons.STYLE.VARIANT.STANDARD,
                          size: cons.SIZE.SMALL,
                          label: { resource: resource, key: 'status' },
                          value: data.status ? data.status : '',
                          disabled: true,
                        }}
                        index={index}
                      />
                    )}
                  </div>
                </Card>
            )
          })
        ) : (
          <Box>
            <UploadImageV2
              {...rest}
              picture={null}
              type={type}
              title={{ resource: 'UploadImage', key: 'image' }}
              name="image"
              uploadImagePath={uploadPath}
              saveImage={(name, image) => onSaveImage(tabKey, null, name, image)}
              disableInput={disableInput}
            />
          </Box>
        )} */}
      </div>
    )
  }

  const isProduct = (typeof type === 'string' && type.toUpperCase() === 'PRODUCT')

  return (
    <div>
      {typeList && typeList.length ? (
        <Tabs value={tabIndex} onChange={handleChange} indicatorColor="primary" textColor="primary" variant="scrollable">
          {typeList.map((t, i) => (
            <Tab key={`${(t && (t.value || t.key)) || i}`} label={(t && (t.label || t.value || t.key)) || t} />
          ))}
        </Tabs>
      ) : null}

      <div>
        {typeList && typeList.length && isProduct ? renderProductTab(typeList[tabIndex]) : null}
        {(!isProduct && typeList && typeList.length) && (
          <Box sx={{ pt: 1 }}>
            <UploadImageV2 
              {...rest} 
              title={{ resource: 'UploadImage', key: 'image' }}
              onSave={(img) => onUploadImageList({ tab: null, image: img, action: 'legacySave' })} 
              />
          </Box>
        )}
      </div>
    </div>
  )
}
